/**
 * "Change Persona" confirmation (AC-USER-004).
 *
 * The admin picks the new persona inside the dialog and the consequence copy
 * updates live, so the sentence they confirm is the change they are actually
 * making. Confirming issues `PATCH /users/v1/{id} { persona }`, which is what
 * writes the `rbac.persona_changed` audit row server-side (FR-RBAC-03).
 */
import { useState } from 'react';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { ApiError } from '../../lib/api-error';
import type { TeamUser } from '../../lib/api-types';
import { PERSONA_LABELS, type Persona } from '../../lib/personas';

import { INVITABLE_PERSONAS } from './invite-user-dialog';
import { describePersonaChange } from './persona-consequences';

export interface ChangePersonaDialogProps {
  user: TeamUser;
  onCancel: () => void;
  onConfirm: (persona: Persona) => Promise<void>;
}

export function ChangePersonaDialog({
  user,
  onCancel,
  onConfirm,
}: ChangePersonaDialogProps): JSX.Element {
  const [persona, setPersona] = useState<Persona>(
    user.persona !== null && user.persona !== 'coach' ? 'coach' : 'primary_stat_operator'
  );
  const [error, setError] = useState<string | null>(null);
  const consequence = describePersonaChange(user.email, user.persona, persona);

  return (
    <ConfirmDialog
      title="Change Persona"
      confirmLabel="Change"
      error={error}
      isConfirmDisabled={persona === user.persona}
      consequence={
        <>
          {consequence.summary}
          {consequence.gained.length > 0 ? (
            <span className="confirm-dialog__delta confirm-dialog__delta--gain">
              {' '}
              This user will be able to {consequence.gained.join(', ')}.
            </span>
          ) : null}
          {consequence.lost.length > 0 ? (
            <span className="confirm-dialog__delta confirm-dialog__delta--loss">
              {' '}
              This user will lose the ability to {consequence.lost.join(', ')}.
            </span>
          ) : null}
          {consequence.notes.map((note) => (
            <span className="confirm-dialog__delta" key={note}>
              {' '}
              {note}
            </span>
          ))}
        </>
      }
      onCancel={onCancel}
      onConfirm={async () => {
        setError(null);
        try {
          await onConfirm(persona);
        } catch (caught) {
          setError(
            ApiError.isApiError(caught)
              ? caught.message
              : 'The persona could not be changed. Please try again.'
          );
        }
      }}
    >
      <div className="field">
        <label className="field__label" htmlFor="change-persona-select">
          New persona
        </label>
        <select
          id="change-persona-select"
          className="field__control"
          value={persona}
          onChange={(event) => setPersona(event.target.value as Persona)}
        >
          {INVITABLE_PERSONAS.map((option) => (
            <option key={option} value={option} disabled={option === user.persona}>
              {PERSONA_LABELS[option]}
              {option === user.persona ? ' (current)' : ''}
            </option>
          ))}
        </select>
      </div>
    </ConfirmDialog>
  );
}
